import React, { useState } from 'react';
import { arrayOf, bool, func, node, object, shape, string } from 'prop-types';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionArticle.module.css';

// Section component that's able to show article content
// The article content is mainly supposed to be inside a block
const SectionArticle = props => {
  const {
    sectionId,
    sectionName,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    blocks,
    isInsideContainer,
    options,
    leftTitle,
    currentUser,
    listings,
  } = props;

  // State for seller ID and website domain inputs (used when section has -code in its ID or name)
  const [sellerId, setSellerId] = useState('');
  const [websiteDomain, setWebsiteDomain] = useState('');

  // Function to extract clean domain from URL
  const extractDomain = input => {
    if (!input) return '';

    let domain = input.trim();

    // If it starts with http:// or https://, parse as URL
    if (domain.startsWith('http://') || domain.startsWith('https://')) {
      try {
        const url = new URL(domain);
        domain = url.hostname;
      } catch (error) {
        // If URL parsing fails, try to extract manually
        domain = domain.replace(/^https?:\/\//, '');
        domain = domain.split('/')[0]; // Remove path
        domain = domain.split('?')[0]; // Remove query params
        domain = domain.split('#')[0]; // Remove hash
      }
    }

    // Remove www. prefix if present
    if (domain.startsWith('www.')) {
      domain = domain.substring(4);
    }

    // Extract main domain (remove subdomains)
    const parts = domain.split('.');
    if (parts.length > 2) {
      // Keep only the last two parts (domain + extension)
      domain = parts.slice(-2).join('.');
    }

    return domain;
  };

  // Handle website domain input with URL processing
  const handleWebsiteDomainChange = e => {
    const inputValue = e.target.value;
    const cleanDomain = extractDomain(inputValue);
    setWebsiteDomain(cleanDomain);
  };

  // Check if this section should show seller ID input (has -code in sectionId or sectionName)
  const isCodeSection =
    (sectionId && sectionId.includes('-code')) || (sectionName && sectionName.includes('-code'));
  console.log('isCodeSection', isCodeSection);
  console.log('sectionId', sectionId);
  console.log('sectionName', sectionName);

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />

          {/* Show seller ID and website domain inputs for -code sections */}
          {isCodeSection ? (
            <div className={css.sellerIdContainer}>
              <div className={css.inputGroup}>
                <h4 className={css.embedTitle}>Store Configuration</h4>
                <div className={css.inputRow}>
                  <input
                    id={`${sectionId}-sellerId`}
                    name="sellerId"
                    type="text"
                    placeholder="Enter Store ID"
                    value={sellerId}
                    onChange={e => setSellerId(e.target.value)}
                    className={css.sellerIdInput}
                  />
                  <input
                    id={`${sectionId}-websiteDomain`}
                    name="websiteDomain"
                    type="text"
                    placeholder="Enter Website Domain (e.g., https://yourstore.com or yourstore.com)"
                    value={websiteDomain}
                    onChange={handleWebsiteDomainChange}
                    className={css.websiteDomainInput}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </header>
      ) : null}
      {hasBlocks ? (
        <div
          className={classNames(defaultClasses.blockContainer, css.articleMain, {
            [css.noSidePaddings]: isInsideContainer,
          })}
        >
          <BlockBuilder
            blocks={blocks}
            sectionId={sectionId}
            ctaButtonClass={defaultClasses.ctaButton}
            options={options}
            currentUser={currentUser}
            listings={listings}
            sellerId={isCodeSection ? sellerId : undefined}
            websiteDomain={isCodeSection ? websiteDomain : undefined}
          />
        </div>
      ) : null}
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionArticle.defaultProps = {
  sectionName: null,
  className: null,
  rootClassName: null,
  defaultClasses: null,
  textClassName: null,
  title: null,
  description: null,
  appearance: null,
  callToAction: null,
  blocks: [],
  isInsideContainer: false,
  options: null,
};

SectionArticle.propTypes = {
  sectionId: string.isRequired,
  sectionName: string,
  className: string,
  rootClassName: string,
  defaultClasses: shape({
    sectionDetails: string,
    title: string,
    description: string,
    ctaButton: string,
  }),
  title: object,
  description: object,
  appearance: object,
  callToAction: object,
  blocks: arrayOf(object),
  isInsideContainer: bool,
  options: propTypeOption,
};

export default SectionArticle;
